import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import { SITE_URL } from "@/lib/seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { articleImage } from "@/mocks/categories";

const faqItems = [
  { q: "afaq.q1", a: "afaq.a1" },
  { q: "afaq.q2", a: "afaq.a2" },
  { q: "afaq.q3", a: "afaq.a3" },
  { q: "afaq.q4", a: "afaq.a4" },
];

const whyItems = ["why.list1", "why.list2", "why.list3", "why.list4", "why.list5"];
const stepsItems = ["use.step1", "use.step2", "use.step3", "use.step4", "use.step5"];
const tipsItems = ["tips.list1", "tips.list2", "tips.list3", "tips.list4", "tips.list5"];

export default function Article() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const published = "June 10, 2026";
  const updated = "August 20, 2026";
  const readingTime = "7";

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: t("title"),
          description: t("subtitle"),
          image: articleImage,
          datePublished: "2026-06-10",
          dateModified: "2026-08-20",
          author: { "@type": "Organization", name: "USFansSheet" },
          publisher: {
            "@type": "Organization",
            name: "USFansSheet",
            logo: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg",
          },
          mainEntityOfPage: `${SITE_URL}/article`,
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("breadcrumbHome"),
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t("breadcrumbCurrent"),
              item: `${SITE_URL}/article`,
            },
          ],
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: t(item.q),
            acceptedAnswer: { "@type": "Answer", text: t(item.a) },
          })),
        }}
      />
      <SiteHeader />
      <main className="pt-24 md:pt-32 pb-14 md:pb-20">
        <article className="mx-auto max-w-3xl px-4 md:px-6">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              {t("breadcrumbHome")}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400">{t("breadcrumbCurrent")}</span>
          </nav>

          {/* Header */}
          <header className="mt-6">
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-book-open-line text-sm"></i>
              {t("category")}
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold leading-tight text-foreground-950">
              {t("title")}
            </h1>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-foreground-600">{t("subtitle")}</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {t("published")}: {published}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                {t("updated")}: {updated}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {readingTime} {t("readingTime")}
              </span>
            </div>
          </header>

          {/* Cover image */}
          <div className="mt-8 h-56 md:h-96 w-full overflow-hidden rounded-2xl">
            <img
              src={articleImage}
              alt="USFans Spreadsheet guide to smarter product research"
              title="USFans Spreadsheet beginner guide"
              className="h-full w-full object-cover object-top"
            />
          </div>

          {/* Intro */}
          <div className="mt-8 space-y-4 text-base md:text-lg leading-relaxed text-foreground-700">
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
          </div>

          {/* What is */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("h2.what")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("p.what1")}</p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("p.what2")}</p>
          </section>

          {/* Why */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("h2.why")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("p.why1")}</p>
            <ul className="mt-4 space-y-3">
              {whyItems.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-foreground-700">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  <span>{t(item)}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Organized */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("h2.organized")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("p.organized1")}</p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("p.organized2")}</p>
          </section>

          {/* Use */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("h2.use")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("p.use1")}</p>
            <ol className="mt-5 space-y-4">
              {stepsItems.map((item, i) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">
                    {i + 1}
                  </span>
                  <span className="text-base text-foreground-700 pt-0.5">{t(item)}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Categories */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("h2.categories")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {t("p.categories1")}{" "}
              <Link to="/#categories" className="font-medium text-primary-600 hover:text-primary-700 underline cursor-pointer">
                {t("nav.categories")}
              </Link>
              .
            </p>
          </section>

          {/* Tips */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("h2.tips")}</h2>
            <ul className="mt-4 space-y-3">
              {tipsItems.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-foreground-700">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                  <span>{t(item)}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("h2.faq")}</h2>
            <div className="mt-6 space-y-3">
              {faqItems.map((item, i) => (
                <div key={item.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900">{t(item.q)}</span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                      <i className={`text-base transition-transform ${openFaq === i ? "rotate-45" : ""} ri-add-line`}></i>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-foreground-600">{t(item.a)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Disclaimer */}
          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
            <p className="text-xs md:text-sm leading-relaxed text-foreground-500">{t("disclaimer")}</p>
          </div>

          {/* CTA */}
          <div className="mt-8 rounded-2xl bg-primary-950 p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">{t("cta.title")}</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-background-100/80">{t("cta.text")}</p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-800 hover:bg-background-100 transition-colors cursor-pointer"
              >
                {t("cta.button")}
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <Link
                to="/"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-100/40 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-background-100/10 transition-colors cursor-pointer"
              >
                {t("backHome")}
              </Link>
            </div>
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}